import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/common/DataTable";
import { PlaceholderPanel } from "@/components/common/PlaceholderPanel";
import { contactService, customerService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Star, MessageSquare } from "lucide-react";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "communication", label: "Communication" },
];

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await contactService.get(id);
      setContact(c);
      if (c.customerId) customerService.get(c.customerId).then(setCustomer).catch(() => {});
    } catch (e) {
      toast.error(apiErrorMessage(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!contact) return <div className="text-muted-foreground">Contact not found.</div>;

  return (
    <div data-testid="contact-detail">
      <button onClick={() => navigate("/admin/contacts")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-contacts">
        <ArrowLeft className="h-4 w-4" /> Back to Contacts
      </button>

      <div className="bg-card border border-border rounded-xl p-6 shadow-sm mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              {contact.fullName}
              {contact.isPrimary && <Star className="h-5 w-5 text-amber-500 fill-amber-500" />}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">{contact.title || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge color={contact.status === "Active" ? "green" : "slate"}>{contact.status}</Badge>
            {customer && (
              <button onClick={() => navigate(`/admin/customers/${customer.id}`)} className="text-sm text-primary hover:underline" data-testid="contact-customer-link">
                {customer.customerName}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b border-border">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`contact-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="contact-overview">
          <Section title="Contact Info">
            <Row label="Email" value={contact.email} />
            <Row label="Mobile" value={contact.mobile} />
            <Row label="Phone" value={contact.phone} />
            <Row label="Department" value={contact.department} />
            <Row label="Preferred Channel" value={contact.preferredChannel} />
          </Section>
          <Section title="Consent">
            <Row label="Email opt-in" value={contact.optInEmail ? "Yes" : "No"} />
            <Row label="SMS opt-in" value={contact.optInSms ? "Yes" : "No"} />
            <Row label="WhatsApp opt-in" value={contact.optInWhatsapp ? "Yes" : "No"} />
          </Section>
        </div>
      )}

      {tab === "communication" && (
        <PlaceholderPanel
          icon={MessageSquare}
          title="Communication History"
          description="Calls, emails, and messages with this contact will appear here once the Omnichannel and Integration modules are enabled."
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground text-right">{value || "—"}</span>
    </div>
  );
}
